import React, { lazy, Suspense, type FC, type ReactNode } from 'react';
import { useIntersectionObserver } from '@benzinga/hooks';
import type { QuoteSessionType, PeerQuote } from '@benzinga/quotes-manager';

const Peers = lazy(() => import('../../../src/components/Quote/Peers').then(module => ({ default: module.Peers })));

interface DrilldownFooterProps {
  isETF: boolean;
  symbol: string;
  leftSection?: ReactNode | null;
  type: QuoteSessionType | null;
  peers: Record<string, PeerQuote>;
  peerSymbols: string[];
}

const DrilldownFooter: FC<DrilldownFooterProps> = ({ isETF, leftSection, peers, peerSymbols, symbol, type }) => {
  const hasLeftSection = leftSection != null && leftSection !== false;
  const eligibleForPeersColumn = !isETF && peerSymbols.length > 0;
  const [suppressPeersColumn, setSuppressPeersColumn] = React.useState(false);
  const showPeersColumn = eligibleForPeersColumn && !suppressPeersColumn;

  React.useLayoutEffect(() => {
    setSuppressPeersColumn(false);
  }, [symbol, isETF, peerSymbols.length]);

  const [footerElement, setFooterElement] = React.useState<HTMLDivElement | null>(null);
  const footerRef = React.useMemo(
    () => ({ current: footerElement }) as React.RefObject<Element>,
    [footerElement],
  );
  const entry = useIntersectionObserver(footerRef, {
    rootMargin: '300px',
    threshold: 0,
    freezeOnceVisible: true,
  });
  const shouldLoadPeers = !!entry?.isIntersecting;

  if (!hasLeftSection && !eligibleForPeersColumn) {
    return null;
  }

  return (
    <div className="mx-auto max-w-[1400px] w-full">
      <div ref={setFooterElement} className="flex flex-col gap-8 lg:flex-row">
        {hasLeftSection ? <div className={showPeersColumn ? 'w-full lg:w-9/12' : 'w-full'}>{leftSection}</div> : null}
        {showPeersColumn ? (
          <div className={`w-full ${hasLeftSection ? 'lg:w-3/12' : 'lg:w-3/12 lg:ml-auto'}`}>
            {shouldLoadPeers ? (
              <Suspense fallback={<div />}>
                <Peers
                  key={symbol}
                  peers={peers}
                  peerSymbols={peerSymbols}
                  onAvailabilityChange={available => {
                    if (!available) setSuppressPeersColumn(true);
                  }}
                  symbol={symbol}
                  type={(type || 'REGULAR') as QuoteSessionType}
                />
              </Suspense>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DrilldownFooter;
